import { useMemo } from 'react';

import type { NavEligibility } from '@/components/app-nav-items';
import { useApplicationAccessDecision } from '@/hooks/use-application-access-decision';
import { useAuth } from '@/lib/auth-guards';
import { isStaffAccount } from '@/lib/human-account';
import type { RouterOutputs } from '@/lib/trpc';

/**
 * The server's application-access decision, taken from the procedure's own
 * inferred output. Never hand-declared in the app.
 */
type ApplicationAccessDecision = RouterOutputs['getApplicationAccess'];

/**
 * ADR 0328. QA is granted only by a Staff account's application-access decision
 * carrying QA Review. Every account `ApplicationAccessGate` admits that is not
 * Staff keeps the clinical workspaces it has today — including an account whose
 * Human Account Type is empty or unknown — so this rule can never hide Patients,
 * Referrals or Visits from a real account. The structural reason it cannot:
 * `ApplicationAccessGate` returns its children for anyone who is not Staff
 * without consulting a decision, so an unresolved decision must not be read as
 * "no clinical workspaces" here.
 */
export function navEligibilityFrom(input: {
  isStaff: boolean;
  decision?: ApplicationAccessDecision;
}): NavEligibility {
  if (!input.isStaff) {
    return { patientIntake: true, qaReview: false };
  }
  return {
    patientIntake: input.decision?.patientIntake ?? false,
    qaReview: input.decision?.qaReview ?? false,
  };
}

/** The one derivation the sidebar, the tab bar, the QA route and the in-QA
 *  links all read, so they cannot disagree (ADR 0328). */
export function useNavEligibility(): NavEligibility {
  const { humanAccountTypes } = useAuth();
  const isStaff = isStaffAccount(humanAccountTypes);
  // The shared query is invoked UNCONDITIONALLY, with `enabled: false` for an
  // account that is not Staff. A hook called only for Staff would break the
  // rules of hooks; a disabled query issues no request, so a Provider — whose
  // gate returns its children before reading anything — adds none.
  const decision = useApplicationAccessDecision(isStaff);
  // Memoised on primitives. The gate's 15 s poll returns a structurally
  // identical decision (TanStack's structural sharing keeps the data reference
  // stable), so the memo returns the SAME object across polls and the sidebar
  // and tab bar do not re-render on every interval.
  return useMemo(
    () => navEligibilityFrom({ isStaff, decision: decision.data }),
    [isStaff, decision.data],
  );
}
